import { defineComponent as _defineComponent } from 'vue'

const __default__ = {
  inheritAttrs: false,
}

import { observeSizeChange } from '@@/bits/resize_observer'
import { uniqueId } from 'lodash-es'
import type { Data } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
/**
 * *Note: Do NOT put CSS transition for height on this element, if you do the autosizing will NOT work.
 * Any style/class prop passsed to this component will be applied to the parent div container,
 * and fallthrough attributes such aria attributes will be passed onto the input element.

 * Compared to the old multiline_input, the '@input' and '@enter' events emit the event object, not the string value.
 *
 * Recommended usage:
 * <OzMultilineInput :value="value" @input="onInput" @enter="onEnter" />
 */

export type OzMultilineInputBorder = 'full' | 'bottom' | 'none'
export type OzMultilineInputFontSize = 14 | 17 | 20


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzMultilineInput',
  props: {
    value: { default: '' },
    disabled: { type: Boolean, default: false },
    border: { default: 'full' },
    fontSize: { default: 17 },
    placeholder: { default: '' },
    readonly: { type: Boolean, default: false },
    dir: { default: 'auto' },
    allowNewlines: { type: Boolean, default: false },
    isValid: { type: Boolean, default: true },
    validationMessage: { default: '' },
    testId: { default: 'multilineInput' },
    minHeight: { default: 0 },
    maxLength: { default: undefined },
    paddingClass: { default: undefined }
  },
  emits: ["input", "enter", "blur", "click", "escape", "focus", "focusout", "keyup", "paste", "arrow-down-on-last-line", "arrow-up-on-first-line"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'input', event: InputEvent): void
  (e: 'enter', event: Event): void
  (e: 'blur', event: Event): void
  (e: 'click', event: Event): void
  (e: 'escape', event: Event): void
  (e: 'focus', event: FocusEvent): void
  (e: 'focusout', event: FocusEvent): void
  (e: 'keyup', event: Event): void
  (e: 'paste', event: ClipboardEvent): void
  (e: 'arrow-down-on-last-line'): void
  (e: 'arrow-up-on-first-line'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string, disabled: boolean, border: OzMultilineInputBorder, fontSize: OzMultilineInputFontSize, placeholder: string, readonly: boolean, dir: 'ltr' | 'rtl' | 'auto', allowNewlines: boolean, isValid: boolean, validationMessage: string, testId: string, minHeight: number, maxLength: number, paddingClass: string }





expose({
  focus: () => {
    nextTick(() => inputElement.value?.focus())
  },
  blur: () => {
    inputElement.value?.blur()
  },
  select: () => {
    nextTick(() => inputElement.value?.select())
  },
})

const {
  class: containerClass,
  style: containerStyle,
  ...otherAttrs
} = useAttrs() as { class?: string; style?: string; otherAttrs: Data }

const inputElement = ref<HTMLTextAreaElement>()
const height = ref<null | number>(null)
const sizeObserver = ref<ResizeObserver | null>(null)
const isInputFocused = ref(false)

const style = computed(() => {
  if (!height.value) return {}
  return { height: `${height.value}px` }
})

const currentValue = ref(props.value)

const VALIDATION_MESSAGE_ID = uniqueId('validationMessageId')

function onFocus(e: Event): void {
  isInputFocused.value = true
  emit('focus', e)
}

function onFocusOut(e: Event): void {
  isInputFocused.value = false
  emit('focusout', e)
}

function onInput(e: Event): void {
  removeNewlinesIfDisallowed()
  emit('input', e)
}

function onEnter(e: KeyboardEvent): void {
  // In languages like Chinese, Japanese, & Korean, users input characters using an IME.
  // If they are in composing mode (usually indicated by an underline under the word),
  // and they press Enter, we could receive 2 keydown events. Most sources say this is
  // a bug. We can ignore one of the 2 events by checking isComposing or keyCode === 229
  // for legacy browsers.
  // https://github.com/vuejs/vue/issues/10277#issuecomment-873337252
  if (e.isComposing || e.keyCode === 229) return
  if (props.allowNewlines) return
  emit('enter', e)
  e.preventDefault()
}

function onArrowDown(): void {
  // emit an event when arrow down key is pressed and the cursor is on the last line of the textarea
  // the default behaviour for arrow down on the last line is move to the end of the line
  // we only emit a special event to be handled if the cursor is already at the end of the last line => check selectionStart === value.length
  if (inputElement.value?.selectionStart === inputElement.value?.value.length) {
    emit('arrow-down-on-last-line')
  }
}

function onArrowUp(): void {
  // emit an event when arrow up key is pressed and the cursor is on the first line of the textarea
  // the default behaviour for arrow up on the first line is move to the start of the line
  // we only emit a special event to be handled if the cursor is already at the start of the first line => check selectionStart === 0
  if (inputElement.value?.selectionStart === 0) {
    emit('arrow-up-on-first-line')
  }
}

function removeNewlinesIfDisallowed(): void {
  if (props.allowNewlines) return
  const sanitizedValue = currentValue.value.replace(/\n|\r/g, '')
  if (currentValue.value !== sanitizedValue) {
    currentValue.value = sanitizedValue
  }
}

function autosize(): void {
  height.value = null // Otherwise scrollHeight will just be the previous height
  nextTick(() => {
    if (inputElement.value) {
      height.value = Math.max(inputElement.value.scrollHeight, props.minHeight)
      if (inputElement.value.scrollHeight === 0) {
        setTimeout(() => {
          if (inputElement.value) {
            height.value = Math.max(inputElement.value.scrollHeight, props.minHeight)
          }
        }, 0)
      }
    }
  })
}

watch(
  () => props.value,
  (newValue) => {
    currentValue.value = newValue || ''
    autosize()
    removeNewlinesIfDisallowed()
  },
  { immediate: true },
)

onMounted(() => {
  sizeObserver.value = observeSizeChange(inputElement.value as HTMLElement, () => autosize())
})

onBeforeUnmount(() => {
  sizeObserver.value?.disconnect()
  sizeObserver.value = null
})

return { __sfc: true,props, emit, containerClass, containerStyle, otherAttrs, inputElement, height, sizeObserver, isInputFocused, style, currentValue, VALIDATION_MESSAGE_ID, onFocus, onFocusOut, onInput, onEnter, onArrowDown, onArrowUp, removeNewlinesIfDisallowed, autosize }
}

})